import { IOperator } from "api/types/_operator";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownMultiOption from "components/Common/DropdownMultiOption";
import DropdownOption from "components/Common/DropdownOption";
import Hashtag from "components/Common/Hashtag";
import TooltipCustom from "components/Common/TooltipCustom";
import RangeDatePicker from "components/pickers/RangeDatePicker";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { postNewsTracking } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    isModal?: boolean;
    id?: string;
    isCopy?: boolean,
    categories?: { value: string, label: string }[],
    operators?: IOperator[],
    isShowLevel1Dropdown?: boolean
    triggerRefresh?: (_option?: { is_refresh_category: boolean, is_refresh_tags: boolean }) => void;
}

registerLocale("en", en);
registerLocale("ko", ko);


const ImportArticleForm = ({
    operators,
    categories = [],
    triggerRefresh,
}: Props) => {
    const { t, i18n } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const _tags: string[] = (values?.tags || [])?.map((e: any) => e?.text);
            const categories_current = (values?.categories || [])?.filter((_c: any) => (!!_c?.value))?.map((_c: any) => ({
                id: Number(_c?.value),
                name: _c?.label || '',
            }));
            const categories_new = (values?.new_categories || [])?.filter((_c: any) => (!!_c?.id))?.map((_c: any) => ({
                id: null,
                name: _c?.id || '',
            }));
            const IS_REFRESH_CATEGORY: boolean = !!categories_new?.length;
            const response: any = await postNewsTracking({
                keyword: values?.keyword || '',
                title: values?.title || '',
                url: values?.url || '',
                published_at: values?.published_at || '',
                admin_id: !!values?.admin_id?.value ? Number(values?.admin_id?.value) : null,
                tags: _tags,
                categories: [...categories_current, ...categories_new]
            });
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh({ is_refresh_category: IS_REFRESH_CATEGORY, is_refresh_tags: !!_tags?.length });
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        keyword: Yup.string().required(`${t("This is required")}`),
        title: Yup.string().required(`${t("This is required")}`),
        url: Yup.string().required(`${t("This is required")}`),
        published_at: Yup.string().nullable(),
        admin_id: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).nullable(),
        categories: Yup.array().of(Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).nullable()).nullable(),
        new_categories: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string(),
            })
        ).nullable(),
        is_create_new_category: Yup.boolean().nullable(),
    });

    const formik = useFormik({
        initialValues: {
            keyword: '',
            title: '',
            url: '',
            published_at: `${moment().format("Y-MM-DD")}`,
            admin_id: null,
            categories: [],
            new_categories: [],
            is_create_new_category: false,
            tags: []
        },
        validationSchema,
        onSubmit: handleSubmit,
    });


    useEffect(() => {
        return () => {
            formik?.setFieldValue('keyword', '');
            formik?.setFieldValue('title', '');
            formik?.setFieldValue('url', '');
            formik?.setFieldValue('admin_id', null);
            formik?.setFieldValue('category_id', null);
            formik?.setFieldValue('category_name', '');
            formik?.setFieldValue('tags', []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <div>
                <Container>
                    <Row>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                                <Row>
                                    <Col sm={12} md={12} lg={12} className='mt-3 mb-1'>
                                        <div>
                                            <label className="form-label">
                                                {t("Category")}
                                            </label>
                                            <DropdownMultiOption
                                                name={`categories`}
                                                dataList={categories}
                                                placeholder={t('Category')}
                                                className="search-filter-category-type dropdown-status-rounded"
                                                classNamePrefix="name-prefix"
                                                initialValue={formik?.values?.categories}
                                                onChangeSelect={(e: any) => {
                                                    formik.setFieldValue(`categories`, e)
                                                }}
                                                isHasOptionAll={false}
                                            />
                                            {(formik.touched.categories as any) && (formik.errors.categories as any)?.value ? (
                                                <div className="text-danger mt-2">{(formik.errors.categories as any)?.value || ''}</div>
                                            ) : null}
                                        </div>
                                    </Col>
                                    <div className="position-relative mb-0 mt-4" style={{ border: `${!!formik?.values?.is_create_new_category ? 1 : 0}px dashed rgb(191, 193, 201)`, borderRadius: '4px', padding: '10px 12px' }}>
                                        <div className='position-absolute' style={{ backgroundColor: "var(--vz-secondary-bg)", top: '0px', transform: 'translateY(-50%)' }}>
                                            <Col sm={12} md={12} lg={12} className='mt-0 px-1'>
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" role="switch" checked={!!formik?.values?.is_create_new_category} id={`is_create_new_category`}
                                                        onChange={(event: any) => {
                                                            formik.setFieldValue(`is_create_new_category`, !!event.target.checked);
                                                            formik.setFieldValue(`new_categories`, []);
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`is_create_new_category`}><span className="text-muted">{t('and')}</span> {t('Create New Category')} {!!formik?.values?.is_create_new_category && <span className="text-danger">*</span>}</label>
                                                </div>
                                            </Col>
                                        </div>
                                        {!!formik?.values?.is_create_new_category && <div className="mb-0">
                                            <Col sm={12} md={12} lg={12} className='mt-2'>
                                                <div>
                                                    <label className="form-label">
                                                        {t("New Category")}
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        className="mb-2"
                                                        id="new_categories"
                                                        name="new_categories"
                                                        autocomplete={false}
                                                        value={((formik?.values?.new_categories || [])?.[0] as any)?.id || ''}
                                                        placeholder={`${t("Category Name")}...`}
                                                        onChange={(event: any) => {
                                                            formik.setFieldValue("new_categories", [{ id: event?.target?.value || '', text: event?.target?.value || '' }]);
                                                        }}
                                                    />
                                                    {(formik.touched.new_categories as any) && (formik.errors.new_categories as any)?.id ? (
                                                        <div className="text-danger mt-2">{(formik.errors.new_categories as any)?.id || ''}</div>
                                                    ) : null}
                                                </div>
                                            </Col>
                                        </div>}
                                    </div>

                                    <Col sm={12} md={12} xl={12} className='mt-2'>
                                        <Label htmlFor="keyword" className="form-label">{t('Keyword')} <span className="text-danger"> *</span></Label>
                                        <Input
                                            name="keyword"
                                            type="text"
                                            className="form-control"
                                            id="keyword"
                                            placeholder={t('Keyword')}
                                            value={formik?.values?.keyword}
                                            onChange={(event: any) => formik.setFieldValue('keyword', event?.target?.value)}
                                        />
                                        {formik.touched.keyword && formik.errors.keyword ? (
                                            <div className="text-danger mt-2">{formik.errors.keyword}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-3 '>
                                        <Label htmlFor="title" className="form-label">{t('Tracking Article Title')} <span className="text-danger"> *</span></Label>
                                        <Input
                                            name="title"
                                            type="text"
                                            className="form-control"
                                            id="title"
                                            // rows={4}
                                            placeholder={t('Tracking Article Title')}
                                            value={formik?.values?.title}
                                            onChange={(event: any) => formik.setFieldValue('title', event?.target?.value)}
                                        />
                                        {formik.touched.title && formik.errors.title ? (
                                            <div className="text-danger mt-2">{formik.errors.title}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-3 '>
                                        <Label htmlFor="url" className="form-label">{t('URL')} <span className="text-danger"> *</span></Label>
                                        <Input
                                            name="url"
                                            type="text"
                                            className="form-control"
                                            id="url"
                                            placeholder={t('URL')}
                                            value={formik?.values?.url}
                                            onChange={(event: any) => formik.setFieldValue('url', event?.target?.value)}
                                        />
                                        {formik.touched.url && formik.errors.url ? (
                                            <div className="text-danger mt-2">{formik.errors.url}</div>
                                        ) : null}
                                    </Col>

                                    <Col sm={12} md={12} xl={12} className='mt-3 '>
                                        <Label htmlFor="admin_id" className="form-label">{t('Registrar')}</Label>
                                        <DropdownOption
                                            name="admin_id"
                                            dataList={(operators || [])?.map((_o: any) => ({ label: `${_o?.first_name}`, value: `${_o?.id}` }))}
                                            placeholder={`${t("Registrar")}...`}
                                            className="dropdown-status-rounded z-hight"
                                            classNamePrefix="name-prefix"
                                            initialValue={formik?.values?.admin_id || null}
                                            onChangeSelect={(e: any) => formik.setFieldValue('admin_id', e)}
                                            isHasOptionAll={true}
                                            optionAll={{ label: '------', value: '' }}
                                        />
                                        {formik.touched.admin_id && formik.errors.admin_id ? (
                                            <div className="text-danger mt-2">{formik.errors.admin_id}</div>
                                        ) : null}
                                    </Col>

                                    <Col lg={12} style={{
                                        borderTop: '1px dashed #bfc1c9',
                                        width: '94%',
                                        margin: '25px auto 0px'
                                    }}></Col>

                                    <Col sm={12} md={12} xl={12} className='mt-3 date-picker-wrapper-custom '>
                                        <Label htmlFor="published_at" className="form-label">{t('Published at')}</Label>
                                        <div>
                                            <RangeDatePicker
                                                dateSearch={formik?.values?.published_at}
                                                maxDate={moment().toDate()}
                                                handleChangeDatePicker={(_date: any) => {
                                                    formik.setFieldValue('published_at', _date ? moment(new Date(_date)).format("Y-MM-DD") : '');
                                                }}
                                            />
                                        </div>
                                    </Col>


                                    <Col lg={12} style={{
                                        borderTop: '1px dashed #bfc1c9',
                                        width: '94%',
                                        margin: '25px auto 0px'
                                    }}></Col>

                                    <Col sm={12} md={12} xl={12} className='mt-3 '>
                                        <div style={{ minHeight: '80px' }}>
                                            <label className="form-label">
                                                {t("Tags")}
                                            </label>
                                            <Hashtag
                                                name="tags"
                                                placeholder={`${t("Press Enter to add a new tag")}...`}
                                                suggestions={[]}
                                                initialValue={formik?.values?.tags || []}
                                                onChangeTag={(event: any) => {
                                                    formik.setFieldValue("tags", event);
                                                }}
                                                idSelected="hashtags-CampaignFormMulti"
                                                maxHashtags={2000}
                                                maxHeight="150px"
                                            />
                                        </div>
                                        {formik.touched.tags && formik.errors.tags ? (
                                            <div className="text-danger mt-2">{(formik.errors.tags?.[0] as any)?.text || ''}</div>
                                        ) : null}
                                    </Col>

                                    <Col sm={12} md={12} xl={12} className='mt-4 pt-1 text-end'>
                                        <button
                                            style={{ width: '190px' }}
                                            type="submit"
                                            disabled={isLoading}
                                            className="btn btn-primary fs-14 rounded-pill">
                                            {isLoading ? <Spinner size="sm me-1" ></Spinner> : <></>}
                                            {t('Button Import Article')}
                                        </button>
                                    </Col>
                                </Row>
                            </div>
                        </form>
                    </Row >
                </Container >
            </div>
            <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />
        </React.Fragment >
    );
};

export default ImportArticleForm;
